import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Loader2, Printer, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const CATEGORY_LABELS = {
  wall: "Walls", floor: "Flooring", ceiling: "Ceiling", lighting: "Lighting",
  plumbing: "Plumbing", electrical: "Electrical", ac: "Air Conditioning",
  doors_windows: "Doors & Windows", painting: "Painting",
  dental_chair: "Dental Chair", xray: "X-Ray Machine", sterilizer: "Sterilizer",
  compressor: "Compressor", suction: "Suction Unit", light: "Chair Light",
  medical_equipment: "Medical Equipment",
  it_equipment: "Electronic Device",
  computer: "Computer", printer: "Printer", network: "Network & Internet",
  phone_system: "Phone System", software: "Software",
  security_camera: "Security Cameras", access_control: "Access Control",
  other: "Other",
};

const ELEMENT_TYPE_LABELS = {
  structural: "Structural",
  medical: "Medical",
  it: "Information Technology",
};

const PRIORITY_META = {
  low:    { label: "Low",    color: "#059669", bg: "#ECFDF5" },
  medium: { label: "Medium", color: "#0284C7", bg: "#EFF6FF" },
  high:   { label: "High",   color: "#EA580C", bg: "#FFF7ED" },
  urgent: { label: "Urgent", color: "#DC2626", bg: "#FEF2F2" },
};

const STATUS_META = {
  pending:      { label: "Pending",       color: "#D97706", bg: "#FFFBEB" },
  in_progress:  { label: "In Progress",   color: "#2563EB", bg: "#EFF6FF" },
  awaiting_approval: { label: "Awaiting Approval", color: "#7C3AED", bg: "#F5F3FF" },
  completed:    { label: "Completed",     color: "#059669", bg: "#ECFDF5" },
  cancelled:    { label: "Cancelled",     color: "#DC2626", bg: "#FEF2F2" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
};

const dateOnly = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const timeOnly = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }); }
  catch { return iso; }
};

const MaintenancePrint = () => {
  const { requestId } = useParams();
  const { token } = useAuth();
  const [req, setReq] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [reqRes, timelineRes] = await Promise.all([
          axios.get(`${API}/maintenance/${requestId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/maintenance/${requestId}/timeline`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        ]);
        setReq(reqRes.data);
        setTimeline(Array.isArray(timelineRes.data) ? timelineRes.data : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [requestId, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#655dc6]" />
      </div>
    );
  }
  if (!req) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-3">
        <p className="text-slate-500">Request not found.</p>
        <Button onClick={() => window.close()} variant="outline"><ArrowLeft className="w-4 h-4 mr-1" /> Close</Button>
      </div>
    );
  }

  const category = CATEGORY_LABELS[req.category] || req.category || "—";
  const elementType = ELEMENT_TYPE_LABELS[req.element_type] || req.element_type || "—";
  const equipmentName = req.device_name || category;
  const equipId = req.biomed_no || req.equipment_id || req.id?.slice(0, 8).toUpperCase();
  const priority = PRIORITY_META[req.priority] || { label: req.priority || "—", color: "#475569", bg: "#F1F5F9" };
  const status = STATUS_META[req.status] || { label: req.status || "—", color: "#475569", bg: "#F1F5F9" };
  const location = [req.clinic_name, req.location_name].filter(Boolean).join(" / ") || "—";
  const floor = req.clinic_floor || req.location_floor || "—";
  const building = req.clinic_building || req.building || "—";
  const images = Array.isArray(req.images) ? req.images.filter(Boolean).slice(0, 6) : [];
  const requestNumber = req.request_number || req.id?.slice(0, 8).toUpperCase() || "—";

  return (
    <div dir="ltr" className="bg-[#F5F5F7] text-slate-900 print-page screen-min-h">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 8mm; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          /* Restore visibility (overrides App.css global rule that hides everything on print) */
          .print-page, .print-page * { visibility: visible !important; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          .print-page {
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            position: relative !important;
            inset: auto !important;
          }
          .doc {
            box-shadow: none !important;
            margin: 0 auto !important;
            padding: 0 !important;
            border-radius: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            background: white !important;
          }
          .card {
            border: 1px solid #CBD5E1 !important;
            background: #FFFFFF !important;
            padding: 6px 9px !important;
            page-break-inside: avoid !important;
          }
          .accent-bar {
            background: #655dc6 !important;
            background-image: linear-gradient(90deg, #655dc6 0%, #9b8fd4 100%) !important;
            height: 3px !important;
          }
          .section-title {
            color: #4a3fa8 !important;
            font-size: 8.5px !important;
            letter-spacing: 1px !important;
            margin-bottom: 4px !important;
            padding-bottom: 3px !important;
            border-bottom: 1.5px solid #E2E8F0 !important;
          }
          .kv-grid { font-size: 9.5px !important; gap: 3px 10px !important; grid-template-columns: 90px 1fr !important; }
          .pill { font-size: 8.5px !important; padding: 2px 8px !important; }
          h1 { font-size: 11px !important; }
          h2 { font-size: 15px !important; }
          .doc-title-sub { font-size: 9px !important; }
          .print-p-tight { padding: 4px 8px !important; }
          .print-mb-2 { margin-bottom: 6px !important; }
          .print-mb-3 { margin-bottom: 8px !important; }
          .print-gap-2 { gap: 6px !important; }
          .print-gap-3 { gap: 8px !important; }
          .photos-grid { grid-template-columns: repeat(4, 1fr) !important; gap: 4px !important; }
          .photos-grid > div { aspect-ratio: 4 / 3 !important; }
          .signatures { margin-top: 14px !important; gap: 14px !important; }
          .signatures .sig-line { height: 24px !important; }
          .timeline-card { padding: 6px 9px !important; page-break-inside: auto !important; }
          .timeline-card .timeline-item { margin-bottom: 3px !important; page-break-inside: avoid !important; }
          .timeline-card .timeline-item p { font-size: 8.5px !important; line-height: 1.3 !important; margin: 0 !important; }
          .timeline-card .timeline-item span.absolute { width: 7px !important; height: 7px !important; left: -18px !important; top: 2px !important; }
          .timeline-card .space-y-3 > * + * { margin-top: 3px !important; }
          .print-footer { font-size: 7.5px !important; margin-top: 8px !important; padding-top: 4px !important; }
          .print-header { padding-bottom: 4px !important; }
          .logo-img { height: 30px !important; }
          .desc-text { font-size: 10px !important; line-height: 1.4 !important; }
        }
        .print-page { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; }
        @media screen { .screen-min-h { min-height: 100vh; } }
        .accent-bar { background: linear-gradient(90deg, #655dc6 0%, #9b8fd4 100%); }
        .kv-grid { display: grid; grid-template-columns: 160px 1fr; gap: 10px 14px; font-size: 12.5px; }
        .kv-grid dt { color: #64748B; font-weight: 500; }
        .kv-grid dd { color: #0F172A; font-weight: 600; }
        .section-title {
          font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px;
          color: #655dc6; font-weight: 700; margin-bottom: 8px;
          padding-bottom: 6px; border-bottom: 2px solid #E2E8F0;
        }
        .pill {
          display: inline-flex; align-items: center; padding: 3px 10px;
          border-radius: 999px; font-size: 11px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .card { background: white; border: 1px solid #E2E8F0; border-radius: 10px; padding: 18px; }
      `}</style>

      {/* Toolbar (hidden on print) */}
      <div className="no-print sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-3 flex justify-between items-center">
          <Button onClick={() => window.close()} variant="ghost" size="sm" className="text-slate-500">
            <ArrowLeft className="w-4 h-4 mr-1" /> Close
          </Button>
          <Button onClick={() => window.print()} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2" data-testid="print-maintenance-btn">
            <Printer className="w-4 h-4" /> Print
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 my-4 doc bg-white rounded-lg shadow-sm">
        {/* Header (using div to avoid App.css print rule that hides <header>) */}
        <div className="print-header">
          <div className="flex justify-between items-start pb-5">
            <div className="flex items-center gap-4">
              <img src="/lucent-logo.png" alt="Lucent" className="h-14 logo-img" />
              <div>
                <h1 className="text-[15px] font-bold text-[#4a3fa8] tracking-wide leading-tight">LUCENT DENTAL CENTER</h1>
                <p className="text-[11px] text-slate-500 uppercase tracking-widest doc-title-sub">Facility Management System</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Document</p>
              <p className="text-[13px] font-mono font-bold text-slate-800">#{requestNumber}</p>
              <p className="text-[10px] text-slate-500 mt-1">Issued: {dateOnly(new Date().toISOString())}</p>
            </div>
          </div>
          <div className="accent-bar h-1 rounded-full"></div>
        </div>

        {/* Title + Status Bar */}
        <section className="mt-6 mb-5 print-mb-2 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Maintenance Request Form</h2>
            <p className="text-sm text-slate-500 mt-0.5 doc-title-sub">Reported on {fmt(req.created_at)}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="pill" style={{ background: priority.bg, color: priority.color }}>
              Priority · {priority.label}
            </span>
            <span className="pill" style={{ background: status.bg, color: status.color }}>
              {status.label}
            </span>
          </div>
        </section>

        {/* Meta Card — 3 columns */}
        <div className="grid grid-cols-3 gap-3 mb-6 print-mb-2 print-gap-2">
          <div className="card p-3 print-p-tight">
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Date Reported</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{dateOnly(req.created_at)}</p>
          </div>
          <div className="card p-3 print-p-tight">
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Time Reported</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{timeOnly(req.created_at)}</p>
          </div>
          <div className="card p-3 print-p-tight">
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Element Type</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{elementType}</p>
          </div>
        </div>

        {/* Two-column info grid */}
        <div className="grid grid-cols-2 gap-4 mb-6 print-mb-2 print-gap-3">
          <div className="card">
            <p className="section-title">Location Details</p>
            <dl className="kv-grid">
              <dt>Location</dt><dd>{location}</dd>
              <dt>Building</dt><dd>{building}</dd>
              <dt>Floor</dt><dd>{floor}</dd>
              <dt>Type</dt><dd className="capitalize">{req.location_type || "clinic"}</dd>
            </dl>
          </div>
          <div className="card">
            <p className="section-title">Equipment / Asset</p>
            <dl className="kv-grid">
              <dt>Equipment</dt><dd>{equipmentName}</dd>
              <dt>Category</dt><dd>{category}</dd>
              <dt>Asset ID</dt><dd className="font-mono">{equipId}</dd>
              {req.manufacturer && (<><dt>Manufacturer</dt><dd>{req.manufacturer}</dd></>)}
            </dl>
          </div>
        </div>

        {/* Reporter Info */}
        <div className="card mb-6 print-mb-2">
          <p className="section-title">Reporter Information</p>
          <div className="grid grid-cols-3 gap-4 text-sm print-gap-2">
            <div>
              <p className="text-[10px] uppercase text-slate-400 tracking-widest mb-1">Full Name</p>
              <p className="font-semibold text-slate-800">{req.reporter_name || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-400 tracking-widest mb-1">Phone</p>
              <p className="font-mono text-slate-800">{req.reporter_phone || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-400 tracking-widest mb-1">WhatsApp</p>
              <p className="font-mono text-slate-800">{req.reporter_whatsapp || "—"}</p>
            </div>
          </div>
        </div>

        {/* Problem Description */}
        <div className="card mb-6 print-mb-2">
          <p className="section-title">Problem Description</p>
          <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed desc-text">
            {req.description || "—"}
          </p>
        </div>

        {/* Photos */}
        {images.length > 0 && (
          <div className="card mb-6 print-mb-2">
            <p className="section-title">Attached Photos ({images.length})</p>
            <div className="grid grid-cols-3 gap-3 photos-grid">
              {images.map((img, i) => (
                <div key={i} className="aspect-video bg-slate-100 rounded-md overflow-hidden border">
                  <img src={img} alt={`Attachment ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assignment + Action Taken */}
        <div className="grid grid-cols-2 gap-4 mb-6 print-mb-2 print-gap-3">
          <div className="card">
            <p className="section-title">Assignment</p>
            <dl className="kv-grid">
              <dt>Assigned To</dt><dd>{req.assigned_to_name || "Unassigned"}</dd>
              <dt>Assigned Role</dt><dd className="capitalize">{req.assigned_role || "—"}</dd>
              <dt>Assigned At</dt><dd>{req.assigned_at ? fmt(req.assigned_at) : "—"}</dd>
              <dt>Completed At</dt><dd>{req.completed_at ? fmt(req.completed_at) : "—"}</dd>
            </dl>
          </div>
          <div className="card">
            <p className="section-title">Initial Action Taken</p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed desc-text" style={{ minHeight: "60px" }}>
              {req.resolution_notes || req.action_taken || <span className="italic text-slate-400">Not yet recorded</span>}
            </p>
          </div>
        </div>

        {/* Timeline — included in print */}
        {timeline.length > 0 && (
          <div className="card mb-6 print-mb-2 timeline-card">
            <p className="section-title">Activity Timeline ({timeline.length})</p>
            <div className="relative ps-4 border-l-2 border-slate-200 space-y-3">
              {timeline.map((ev, i) => (
                <div key={i} className="relative timeline-item">
                  <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-[#655dc6] border-2 border-white shadow"></span>
                  <p className="text-xs text-slate-500">{fmt(ev.created_at || ev.timestamp)}</p>
                  <p className="text-sm text-slate-800">
                    <span className="font-semibold">{ev.label || ev.action || ev.type || "Update"}</span>
                    {(ev.user || ev.user_name) && <span className="text-slate-500"> · by {ev.user || ev.user_name}</span>}
                  </p>
                  {(ev.details || ev.note) && <p className="text-xs text-slate-600 mt-0.5">{ev.details || ev.note}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signatures */}
        <div className="mt-10 grid grid-cols-3 gap-8 text-sm signatures">
          <div>
            <div className="h-12 border-b-2 border-slate-300 mb-1 sig-line"></div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Reported By</p>
            <p className="text-xs text-slate-400 mt-0.5">{req.reporter_name}</p>
          </div>
          <div>
            <div className="h-12 border-b-2 border-slate-300 mb-1 sig-line"></div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Technician / Signature</p>
            <p className="text-xs text-slate-400 mt-0.5">{req.assigned_to_name || "—"}</p>
          </div>
          <div>
            <div className="h-12 border-b-2 border-slate-300 mb-1 sig-line"></div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Approved / Reviewed By</p>
            <p className="text-xs text-slate-400 mt-0.5">Maintenance Manager</p>
          </div>
        </div>

        {/* Footer (using div to avoid App.css print rule) */}
        <div className="mt-10 pt-4 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-400 print-footer">
          <span>© {new Date().getFullYear()} Lucent Dental Center Company — Confidential</span>
          <span className="font-mono">REF/{requestNumber}</span>
          <span>Generated {fmt(new Date().toISOString())}</span>
        </div>
      </div>
    </div>
  );
};

export default MaintenancePrint;
